/**
 * propagarMiembroOficial.ts
 *
 * Propaga el campo `miembro_oficial` desde asesor/asociado hacia propiedades.
 * Una propiedad es `miembro_oficial: true` si AL MENOS UNO de sus agentes
 * vinculados (asesor_doc_ids) es miembro oficial, O si su inmobiliaria
 * tiene miembros oficiales.
 */

import {
  collection,
  getDocs,
  writeBatch,
  doc,
  query,
  where,
} from 'firebase/firestore';
import { db } from '../firebaseConfig';

interface PropagacionResult {
  totalPropiedades: number;
  marcadasOficial: number;
  marcadasNoOficial: number;
  errors: number;
  inmobiliariasOficiales: number;
}

/**
 * Construye el Set de docIds de miembros oficiales y el Set de inmobiliarias oficiales
 */
async function cargarMiembrosOficiales(): Promise<{
  asesorIds: Set<string>;
  inmobiliariaIds: Set<string>;
}> {
  const asesorIds = new Set<string>();
  const inmobiliariaIds = new Set<string>();

  // Cargar asesores oficiales
  const asesorSnap = await getDocs(
    query(collection(db, 'asesor'), where('miembro_oficial', '==', true))
  );
  asesorSnap.forEach(d => {
    asesorIds.add(d.id);
    const data = d.data();
    // Derivar inmobiliarias oficiales desde asesores
    if (Array.isArray(data.inmobiliaria_doc_ids)) {
      data.inmobiliaria_doc_ids.forEach((id: string) => inmobiliariaIds.add(id));
    }
  });

  // Cargar asociados oficiales
  const asociadoSnap = await getDocs(
    query(collection(db, 'asociado'), where('miembro_oficial', '==', true))
  );
  asociadoSnap.forEach(d => {
    asesorIds.add(d.id); // Incluir asociados en el set general
    const data = d.data();
    if (data.inmobiliaria_doc_id) {
      inmobiliariaIds.add(data.inmobiliaria_doc_id);
    }
  });

  return { asesorIds, inmobiliariaIds };
}

/**
 * Propaga `miembro_oficial` a todas las propiedades activas.
 * Llama esta función después de actualizar el padrón de miembros.
 */
export async function propagarMiembroOficial(
  onProgress?: (msg: string) => void
): Promise<PropagacionResult> {
  const result: PropagacionResult = {
    totalPropiedades: 0,
    marcadasOficial: 0,
    marcadasNoOficial: 0,
    errors: 0,
    inmobiliariasOficiales: 0,
  };

  try {
    onProgress?.('Cargando miembros oficiales...');
    const { asesorIds, inmobiliariaIds } = await cargarMiembrosOficiales();
    result.inmobiliariasOficiales = inmobiliariaIds.size;

    onProgress?.(`${asesorIds.size} miembros oficiales, ${inmobiliariaIds.size} inmobiliarias oficiales`);

    // Cargar TODAS las propiedades activas
    onProgress?.('Cargando propiedades...');
    const propSnap = await getDocs(
      query(collection(db, 'propiedades'), where('estado', '==', 'A'))
    );

    result.totalPropiedades = propSnap.size;
    onProgress?.(`${propSnap.size} propiedades activas encontradas`);

    // Procesar en batches de 500
    const docs = propSnap.docs;
    let batchCount = 0;
    let batch = writeBatch(db);
    let opsInBatch = 0;

    for (const propDoc of docs) {
      const data = propDoc.data();
      const asesorDocIds: string[] = Array.isArray(data.asesor_doc_ids) ? data.asesor_doc_ids : [];
      const inmobiliariaDocId: string = data.inmobiliaria_doc_id || '';

      // Determinar si es oficial
      const esOficial =
        asesorDocIds.some(id => asesorIds.has(id)) ||
        inmobiliariaIds.has(inmobiliariaDocId);

      // Solo actualizar si cambió
      if (data.miembro_oficial !== esOficial) {
        batch.update(doc(db, 'propiedades', propDoc.id), {
          miembro_oficial: esOficial,
        });
        opsInBatch++;
      }

      if (esOficial) {
        result.marcadasOficial++;
      } else {
        result.marcadasNoOficial++;
      }

      // Commit batch cada 499 operaciones (dejar margen)
      if (opsInBatch >= 499) {
        await batch.commit();
        batchCount++;
        onProgress?.(`Batch ${batchCount} completado (${opsInBatch} ops)`);
        batch = writeBatch(db);
        opsInBatch = 0;
      }
    }

    // Commit último batch de propiedades
    if (opsInBatch > 0) {
      await batch.commit();
      batchCount++;
      onProgress?.(`Batch final propiedades ${batchCount} completado (${opsInBatch} ops)`);
    }

    // Propagar a inmobiliarias: marcar las que tienen miembros oficiales
    onProgress?.('Actualizando inmobiliarias...');
    const inmSnap = await getDocs(collection(db, 'inmobiliaria'));
    batch = writeBatch(db);
    opsInBatch = 0;

    for (const inmDoc of inmSnap.docs) {
      const esOficialInm = inmobiliariaIds.has(inmDoc.id);
      const current = inmDoc.data().tiene_miembros_oficiales;
      if (current !== esOficialInm) {
        batch.update(doc(db, 'inmobiliaria', inmDoc.id), {
          tiene_miembros_oficiales: esOficialInm,
        });
        opsInBatch++;
        if (opsInBatch >= 499) {
          await batch.commit();
          batch = writeBatch(db);
          opsInBatch = 0;
        }
      }
    }

    if (opsInBatch > 0) {
      await batch.commit();
      batchCount++;
      onProgress?.(`Batch final ${batchCount} completado (${opsInBatch} ops)`);
    }

    onProgress?.(`Propagación completa: ${result.marcadasOficial} oficiales, ${result.marcadasNoOficial} no oficiales`);
  } catch (err: any) {
    console.error('Error en propagarMiembroOficial:', err);
    result.errors++;
    onProgress?.(`Error: ${err.message}`);
  }

  return result;
}
